<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Sim Cards</h1>
                <v-btn color="black" prepend-icon="mdi-plus" @click="()=>{selectedCard=null; dialogSwitch = true}">Add
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th-btn :sorted-column="sortedColumn??null" column="vendor_name"
                                sortable title="Vendor" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="iccid"
                                sortable title="ICCID" @sort="sort"/>
                        <th class="text-left">Device Serial</th>
                        <th-btn :sorted-column="sortedColumn??null" column="eid"
                                sortable title="EID" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="imsi"
                                sortable title="IMSI" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="msisdn"
                                sortable title="MSISDN" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="type"
                                sortable title="Type" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="status"
                                sortable title="Status" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="last_received_date"
                                sortable title="Last Received Date" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="purchased_date"
                                sortable title="Purchased Date" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="expiry_date"
                                sortable title="Expiry Date" @sort="sort"/>
                        <th-btn :sorted-column="sortedColumn??null" column="created_at"
                                sortable title="Created at" @sort="sort"/>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="card in cards" v-if="cards.length" :key="card.id" class="text-left">
                        <td>{{ card.vendor_name }}</td>
                        <td>{{ card.iccid }}</td>
                        <td>
                            <a v-if="card.device_slno" class="text-primary text-decoration-none cursor-pointer"
                               @click="goToDevice(card.device_slno)">{{ card.device_slno }}</a>
                            <span v-else>-</span>
                        </td>
                        <td>{{ card.eid }}</td>
                        <td>{{ card.imsi }}</td>
                        <td>{{ card.msisdn }}</td>
                        <td>{{ types[card.type] }}</td>
                        <td>{{ card.type ? 'Active' : 'InActive' }}</td>
                        <td>{{ card.last_received_date }}</td>
                        <td>{{ card.purchased_date }}</td>
                        <td>{{ card.expiry_date }}</td>
                        <td>{{ formatDateTime(card.created_at) }}</td>
                        <td>
                            <div class="d-flex align-center">
                                <v-btn-group density="compact" divided variant="outlined">
                                    <v-btn class="text-warning" size="x-small"
                                           @click="editCard(card)">Edit
                                    </v-btn>
                                    <v-btn class="text-error" size="x-small"
                                           @click="deleteCard(card)">Delete
                                    </v-btn>
                                </v-btn-group>
                                <v-menu location="bottom end">
                                    <template #activator="{ props }">
                                        <v-btn v-bind="props" icon="mdi-dots-vertical"
                                               size="x-small" variant="text" class="ms-1"/>
                                    </template>
                                    <v-list density="compact">
                                        <v-list-item prepend-icon="mdi-sim" title="Check Status & Usage"
                                                     @click="checkSimState(card)"/>
                                    </v-list>
                                </v-menu>
                            </div>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="13">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="13">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @click="fetchCards"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <Create_Edit v-if="dialogSwitch"
                 v-model="dialogSwitch"
                 :card="selectedCard"
                 @addCard="addCard"
                 @updateCard="updateCard"/>

    <v-dialog v-model="simStateDialog" max-width="520">
        <v-card rounded="lg">
            <v-card-title class="d-flex align-center">
                <span class="text-h6">SIM Status &amp; Usage</span>
                <v-spacer/>
                <v-btn icon="mdi-close" variant="text" size="small" @click="simStateDialog = false"/>
            </v-card-title>
            <v-card-subtitle v-if="simStateIccid">ICCID {{ simStateIccid }}</v-card-subtitle>
            <v-card-text>
                <div v-if="simStateLoading" class="text-center py-6">
                    <v-progress-circular color="primary" indeterminate/>
                </div>
                <template v-else-if="simState">
                    <div class="text-subtitle-2 font-weight-bold mb-2">Status</div>
                    <v-row dense class="mb-3">
                        <v-col cols="6"><div class="text-caption text-medium-emphasis">Status</div><div>{{ simState.status || simState.device_status || '-' }}</div></v-col>
                        <v-col cols="6"><div class="text-caption text-medium-emphasis">Last updated</div><div>{{ simState.last_updated || '-' }}</div></v-col>
                        <v-col cols="6"><div class="text-caption text-medium-emphasis">Activated</div><div>{{ simState.activation_date || '-' }}</div></v-col>
                        <v-col cols="6"><div class="text-caption text-medium-emphasis">Deactivated</div><div>{{ simState.deactivation_date || '-' }}</div></v-col>
                    </v-row>
                    <div class="text-subtitle-2 font-weight-bold mb-2">Usage</div>
                    <v-row dense>
                        <v-col cols="6"><div class="text-caption text-medium-emphasis">Data</div><div>{{ simState.current_quota || '-' }}</div></v-col>
                        <v-col cols="6"><div class="text-caption text-medium-emphasis">SMS</div><div>{{ simState.current_quota_SMS ?? '-' }}</div></v-col>
                        <v-col cols="6"><div class="text-caption text-medium-emphasis">Voice</div><div>{{ simState.voice_usage ?? '-' }}</div></v-col>
                        <v-col cols="6"><div class="text-caption text-medium-emphasis">Sessions</div><div>{{ simState.session_count ?? '-' }}</div></v-col>
                    </v-row>
                    <v-divider class="my-3"/>
                    <div class="text-subtitle-2 font-weight-bold mb-2">Raw data (debug)</div>
                    <v-table density="compact" class="text-caption">
                        <tbody>
                        <tr v-for="(val, key) in simState" :key="key">
                            <td class="font-weight-medium" style="white-space: nowrap; vertical-align: top">{{ key }}</td>
                            <td style="word-break: break-all">{{ formatDebugVal(val) }}</td>
                        </tr>
                        </tbody>
                    </v-table>
                </template>
                <div v-else class="text-medium-emphasis py-4">No data returned.</div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import Create_Edit from "@/components/dialogs/sim-cards/Create_Edit.vue";
import ThBtn from "@/components/globals/th-btn.vue";

const cards = ref([]);
const types = {
    'E': 'Embedded',
    'N': 'Nano',
    'M': 'Micro',
    'R': 'Regular',
};
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const sortedType = ref(null);
const sortedColumn = ref(null);
const selectedCard = ref(null);
const dialogSwitch = ref(false);
const router = useRouter();
const {showSnackbar} = useSnackbarStore();

const simStateDialog = ref(false);
const simState = ref(null);
const simStateLoading = ref(false);
const simStateIccid = ref('');

const goToDevice = (slno) => router.push({name: 'device', params: {slno}});

const formatDebugVal = (val) => {
    if (val === null || val === undefined || val === '') return '-';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
};

const checkSimState = (card) => {
    simStateIccid.value = card.iccid;
    simState.value = null;
    simStateLoading.value = true;
    simStateDialog.value = true;
    send('get', `admin/sim-cards/${card.id}/state`)
        .then((res) => {
            simState.value = res.data.data;
        })
        .catch((err) => {
            simStateDialog.value = false;
            showSnackbar(err?.response?.data?.message || 'Could not fetch SIM status/usage', 'error');
        })
        .finally(() => {
            simStateLoading.value = false;
        });
};

onMounted(() => {
    fetchCards();
});
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchCards();
}
const fetchCards = () => {
    const data = {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
    }
    send('get', `admin/sim-cards`, data)
        .then((response) => {
            cards.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
            loading.value = false;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Sim cards could not be loaded', 'error')
        })
}
const addCard = (card) => {
    cards.value.unshift(card);
}
const editCard = (card) => {
    selectedCard.value = card;
    dialogSwitch.value = true;
}
const updateCard = (card) => {
    cards.value.map((item, index) => {
        if (item.id === card.id) {
            cards.value[index] = card;
        }
    });
}
const deleteCard = (card) => {
    if (confirm(`Are you sure to delete "${card.title}" sim card card?`)) {
        send('delete', `admin/sim-cards/${card.id}`)
            .then((response) => {
                if (response.data.success) {
                    cards.value.map((item, index) => {
                        if (item.id === card.id) {
                            cards.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${card.title}" sim card card could not be deleted`, 'error')
                }
            })
    }
}
</script>
